/**
 * @file BackupConfigForm.tsx
 * @description Настройки бэкапа (phase08.9; phase19.26): включение,
 * расписание, срок хранения, путь, e-mail о сбое и состав (база + бакеты).
 *
 * Настройки хранит API, агент на сервере приложения читает их в начале каждого запуска
 * (`agent-config`), так что UI управляет хостом. Черновик — `backup-draft.ts`,
 * состав — `BackupSourcesFields`. На 360 поля идут колонкой, с md — в две.
 *
 * Шаг 7.1: черновик живёт в `useServerDraft` — перечитывание конфига (запуск,
 * соседняя форма, «Обновить») не стирает несохранённые правки.
 */

import { Save } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { EditorialCard } from "../../ui/EditorialCard";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import {
  alertEmailError,
  backupErrorMessage,
  updateBackupConfig,
  type BackupConfigDto,
} from "../../../lib/api-backups";
import { draftError, draftFromConfig, draftToPayload, type BackupDraft } from "./backup-draft";
import { BackupSourcesFields } from "./BackupSourcesFields";
import { BackupSwitchRow } from "./BackupSwitchRow";
import { useServerDraft } from "./use-server-draft";

interface Props {
  config: BackupConfigDto;
  onSaved: (updated: BackupConfigDto) => void;
}

export function BackupConfigForm({ config, onSaved }: Props) {
  const notify = useNotify();
  const { draft, setDraft, reset, replace } = useServerDraft<BackupDraft>(draftFromConfig(config));
  const [saving, setSaving] = useState(false);

  const patch = (p: Partial<BackupDraft>) => setDraft((d) => ({ ...d, ...p }));
  const emailProblem = alertEmailError(draft.alertEmail);

  async function onSave() {
    const problem = draftError(draft);
    if (problem) {
      notify.error(problem);
      return;
    }
    setSaving(true);
    try {
      const updated = await updateBackupConfig(draftToPayload(draft));
      notify.success("Настройки бэкапа сохранены");
      // Черновик — из ответа сервера: сохранённое больше не «правка».
      replace(draftFromConfig(updated));
      onSaved(updated);
    } catch (err) {
      notify.error(backupErrorMessage(err, "Не удалось сохранить настройки"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Настройки"
        subtitle="Агент на сервере приложения читает их в начале каждого запуска"
      />
      <EditorialCard.Body>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <BackupSwitchRow
            className="md:col-span-2"
            label="Бэкап включён"
            hint="Выключен — запуск по расписанию ничего не копирует"
            checked={draft.isEnabled}
            onChange={(v) => patch({ isEnabled: v })}
            disabled={saving}
          />

          <Field id="bk-cron" label="Расписание (cron)" hint="По умолчанию ежедневно в 03:00. Расписание исполняет таймер на сервере приложения.">
            <Input
              id="bk-cron"
              value={draft.cronExpression}
              onChange={(e) => patch({ cronExpression: e.target.value })}
              placeholder="0 3 * * *"
              className="font-mono"
              disabled={saving}
            />
          </Field>

          <Field id="bk-retention" label="Срок хранения, дней" hint="Копии старше удаляются скриптом.">
            <Input
              id="bk-retention"
              type="number"
              inputMode="numeric"
              min={1}
              max={3650}
              value={draft.retentionDays}
              onChange={(e) => patch({ retentionDays: e.target.value })}
              disabled={saving}
            />
          </Field>

          <Field
            id="bk-email"
            label="E-mail для уведомлений о сбое"
            hint="Пусто — письма не отправляются."
            error={emailProblem}
          >
            <Input
              id="bk-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={draft.alertEmail}
              onChange={(e) => patch({ alertEmail: e.target.value })}
              placeholder="admin@example.ru"
              aria-invalid={emailProblem ? true : undefined}
              disabled={saving}
            />
          </Field>

          <div className="md:col-span-2">
            <BackupSourcesFields
              databaseIncluded={draft.databaseIncluded}
              bucketsIncluded={draft.bucketsIncluded}
              onChange={patch}
              disabled={saving}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={reset}
            disabled={saving}
          >
            Сбросить
          </Button>
          <Button onClick={() => void onSave()} loading={saving}>
            <Save className="size-4" />
            {saving ? "Сохраняем…" : "Сохранить"}
          </Button>
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}

/** Подпись + поле + подсказка или ошибка. */
function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      <span className={error ? "text-xs text-destructive" : "text-xs text-on-surface-variant"}>
        {error ?? hint}
      </span>
    </div>
  );
}
